import type { Match, StandingRow } from '@/data/types';

/**
 * По матчам вычисляет таблицу: по 1 очку за победу, тай-брейк — разница сетов.
 * Учитываются COMPLETED-матчи с двумя реальными игроками и валидным `score.winnerId`.
 * Разница сетов считается только для матчей с summary формата "X:Y"; для прочих
 * (walkover и т.п.) победа/played засчитываются, но setDiff не меняется.
 */
export function computeStandings(matches: Match[], participantIds: string[]): StandingRow[] {
  const rows = new Map<string, StandingRow>();
  for (const id of participantIds) {
    rows.set(id, {
      participantId: id,
      played: 0,
      wins: 0,
      losses: 0,
      points: 0,
      setDiff: 0,
    });
  }

  for (const m of matches) {
    if (m.status !== 'COMPLETED' || !m.score || !m.playerAId || !m.playerBId) continue;
    const sum = m.score.summary.match(/^(\d+):(\d+)$/);
    // Разница сетов считается только для стандартного summary "X:Y";
    // для walkover/bye прибавляем 0, но победа/played засчитываются.
    const aSets = sum ? Number(sum[1]) : 0;
    const bSets = sum ? Number(sum[2]) : 0;

    const a = rows.get(m.playerAId);
    const b = rows.get(m.playerBId);
    if (!a || !b) continue;

    // winnerId должен быть одним из игроков матча — иначе данные повреждены, пропускаем.
    const winnerIsA = m.score.winnerId === m.playerAId;
    const winnerIsB = m.score.winnerId === m.playerBId;
    if (!winnerIsA && !winnerIsB) continue;

    a.played++;
    b.played++;
    a.setDiff += aSets - bSets;
    b.setDiff += bSets - aSets;
    if (winnerIsA) {
      a.wins++;
      a.points++;
      b.losses++;
    } else {
      b.wins++;
      b.points++;
      a.losses++;
    }
  }

  return Array.from(rows.values()).sort((x, y) => {
    if (y.points !== x.points) return y.points - x.points;
    if (y.setDiff !== x.setDiff) return y.setDiff - x.setDiff;
    return x.participantId.localeCompare(y.participantId);
  });
}
